import React, { useEffect, useMemo } from 'react';
import Faq from '../../components/Faq';
import {
  COMMITMENT_MONTHS,
  MONTHLY_PRICE,
  OFFER_FAQ,
  OFFER_FORM_PATH,
  OfferFinalCta,
  OfferMobileCtaBar,
  OfferPricing,
  OfferReferences,
  OfferSteps,
  OfferTrust,
  SERVICE_AUDIENCE,
  SERVICE_TYPE,
  SETUP_PRICE,
  WebsiteLocalHero,
  WebsiteLocalIntro,
  WebsiteLocalNearby,
} from '../../components/WebsiteOffer';
import { getWebsiteLocalPage } from '../../data/websiteLocalPages';
import type { WebsiteLocalSlug } from '../../data/websiteLocalPages';
import { useSeoMeta } from '../../hooks/useSeoMeta';
import { trackWebsiteOfferCta } from '../../utils/analytics';
import { buildFaqSchema, buildWebsiteServiceSchema } from '../../utils/structuredData';

/** Nombre de questions générales reprises après les questions locales. */
const GENERAL_FAQ_COUNT = 5;

const trackFaqContact = (): void => trackWebsiteOfferCta('faq', 'form');

interface WebsiteLocalPageProps {
  slug: WebsiteLocalSlug;
}

const WebsiteLocalPage: React.FC<WebsiteLocalPageProps> = ({ slug }) => {
  const page = getWebsiteLocalPage(slug);
  const faqItems = useMemo(() => [...page.faqs, ...OFFER_FAQ.slice(0, GENERAL_FAQ_COUNT)], [page]);
  const jsonLd = useMemo(
    () => [
      buildWebsiteServiceSchema({
        name: page.h1,
        serviceType: SERVICE_TYPE,
        path: page.path,
        audience: SERVICE_AUDIENCE,
        areas: [page.areaName, ...page.nearbyTowns],
        setupPrice: SETUP_PRICE,
        setupDescription: 'Création et mise en ligne',
        monthlyPrice: MONTHLY_PRICE,
        monthlyDescription: 'Maintenance, mises à jour et modifications mensuelles (hébergement et nom de domaine non inclus)',
        commitmentMonths: COMMITMENT_MONTHS,
      }),
      buildFaqSchema(faqItems),
    ],
    [page, faqItems],
  );

  useSeoMeta({ title: page.metaTitle, description: page.metaDescription, path: page.path, jsonLd });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div className="pt-24 md:pt-32 pb-24 md:pb-0">
      <WebsiteLocalHero page={page} />
      <WebsiteLocalIntro page={page} />
      <OfferSteps />
      <OfferPricing />
      <OfferTrust />
      <OfferReferences />
      <Faq showLink={false} items={faqItems} contactTo={OFFER_FORM_PATH} onContactClick={trackFaqContact} />
      <WebsiteLocalNearby page={page} />
      <OfferFinalCta />
      <OfferMobileCtaBar />
    </div>
  );
};

export default WebsiteLocalPage;
